import { useSelector, shallowEqual } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { selectors as meterTypeSelectors } from 'state/meter-type/meter-type-slice';
import { selectors as meterpointValidationSelectors } from 'state/meterpoint-validation/meterpoint-validation-slice';
import {
  METER_TYPE_ERROR_CODES,
  PARTNERS,
  UNSUPPORTED_METER_ERRORS
} from 'config/app-config';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { selectIsDomesticConfirmed } from 'state/settings/settings-slice';
import { TMeter, TMeterError } from 'api/models/TMeter';
import { usePartner } from 'components/app/routes/usePartner';

const includesErrorCodes = (errors, errorCodeList) =>
  errors.some(({ code }) => errorCodeList.includes(code));

export type TUseMeterType = {
  electricityMeter?: TMeter;
  electricityMeters?: TMeter[];
  gasMeter?: TMeter;
  hasElectricityMeter: boolean;
  hasGasMeter: boolean;
  hasE7Meter: boolean;
  hasElectricityTOUMeter?: boolean;
  hasAnyPrepayMeter?: boolean;
  hasAnyTOUMeter?: boolean;
  hasElectricityPrepayMeter?: boolean;
  hasRelatedElectricityMeters?: boolean;
  hasGasPrepayMeter?: boolean;
  isGasPrepayMeter?: boolean;
  isElecPrepayMeter?: boolean;
  hasGasTOUMeter?: boolean;
  isUnsupportedElectricityMeter: boolean;
  isEvException?: boolean;
  isUneligibleElectricityMeter?: boolean;
  isUnsupportedGasMeter: boolean;
  isUneligibleGasMeter?: boolean;
  meterTypeErrors: TMeterError[];
  allMeterErrors?: TMeterError[];
  electricityErrors?: TMeterError[];
  gasErrors?: TMeterError[];
  isBGECustomer?: boolean;
  isMeterpointValidationRequired?: boolean;
  isBGCustomer?: boolean;
  isMeterNonBGExportException?: boolean;
  isMeterpointBGExportException?: boolean;
  selectedFuelErrors: TMeterError[];
  isBGCustomerForElec?: boolean;
  isBGECustomerForElec: boolean;
  isBGECustomerForGas: boolean;
  isBGCustomerForGas?: boolean;
  isRelatedElectricityMeter: boolean;
  regionCode?: string;
};

export const useMeterSelectors = () => {
  const {
    selectValidationRequired,
    selectUserMeterpoints,
    selectElectricityMpxn,
    selectGasMpxn
  } = meterpointValidationSelectors;
  const meterpointValidationRequired = useSelector(
    selectValidationRequired,
    shallowEqual
  );
  const { MPAN, MPRN } = useSelector(selectUserMeterpoints, shallowEqual);
  const hasValidatedElecMeters = useSelector(
    selectElectricityMpxn,
    shallowEqual
  )?.length;
  const hasValidatedGasMeters = useSelector(selectGasMpxn, shallowEqual)
    ?.length;
  const isMeterpointValidationRequired = Object.values(
    meterpointValidationRequired
  ).some((v) => v);

  const electricityMeterSelectors =
    MPAN || hasValidatedElecMeters
      ? meterpointValidationSelectors
      : meterTypeSelectors;
  const gasMeterSelectors =
    MPRN || hasValidatedGasMeters
      ? meterpointValidationSelectors
      : meterTypeSelectors;
  const commonSelectors =
    MPAN || MPRN || hasValidatedElecMeters || hasValidatedGasMeters
      ? meterpointValidationSelectors
      : meterTypeSelectors;

  const {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeterDeprecated,
    selectHasRelatedElectricityMeters,
    selectElectricityMeters
  } = electricityMeterSelectors;

  const {
    selectGasMeter,
    selectGasErrors,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter
  } = gasMeterSelectors;

  const {
    selectErrors,
    selectIsBGECustomer,
    selectIsBGCustomer,
    selectMeterNonBGExportException,
    selectMeterBGExportException
  } = commonSelectors;

  return {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectIsRelatedElectricityMeterDeprecated,
    selectHasRelatedElectricityMeters,
    selectElectricityMeters,
    selectGasMeter,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter,
    selectGasErrors,
    isMeterpointValidationRequired,
    selectErrors,
    selectIsBGECustomer,
    selectMeterNonBGExportException,
    selectMeterBGExportException,
    selectIsBGCustomer
  };
};

export const useMeterType = (): TUseMeterType => {
  const {
    selectElectricityMeter,
    selectHasE7Meter,
    selectHasElectricityTOUMeter,
    selectHasElectricityPrepayMeter,
    selectElectricityErrors,
    selectMeterNonBGExportException,
    selectMeterBGExportException,
    selectIsRelatedElectricityMeterDeprecated,
    selectHasRelatedElectricityMeters,
    selectElectricityMeters,
    selectGasMeter,
    selectHasGasPrepayMeter,
    selectHasGasTOUMeter,
    selectGasErrors,
    isMeterpointValidationRequired,
    selectIsBGECustomer,
    selectIsBGCustomer
  } = useMeterSelectors();
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const isDomesticConfirmed = useSelector(
    selectIsDomesticConfirmed,
    shallowEqual
  );
  const { releaseNonDomesticMeterValidation } = useFlags();
  const { activePartner } = usePartner();
  const isNonDomesticTransferJourney =
    activePartner[PARTNERS.NONDOMESTIC_TRANSFER];
  const unsupportedMeterErrors = UNSUPPORTED_METER_ERRORS.filter((error) => {
    if (!releaseNonDomesticMeterValidation || isNonDomesticTransferJourney) {
      return (
        error !== METER_TYPE_ERROR_CODES.MeterNonDomesticClassException &&
        error !== METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGBException
      );
    }

    if (error === METER_TYPE_ERROR_CODES.MeterNonDomesticClassException) {
      return !isDomesticConfirmed;
    }

    return true;
  });

  const UNELIGIBLE_METER_ERRORS = [METER_TYPE_ERROR_CODES.MeterEVException];

  const electricityMeters = useSelector(selectElectricityMeters, shallowEqual);
  const hasRelatedElectricityMeters = useSelector(
    selectHasRelatedElectricityMeters,
    shallowEqual
  );
  const electricityMeter = useSelector(selectElectricityMeter, shallowEqual);
  const isRelatedElectricityMeter = useSelector(
    selectIsRelatedElectricityMeterDeprecated,
    shallowEqual
  );
  const gasMeter = useSelector(selectGasMeter, shallowEqual);
  const isElecPrepayMeter = useSelector(
    selectHasElectricityPrepayMeter,
    shallowEqual
  );
  const isGasPrepayMeter = useSelector(selectHasGasPrepayMeter, shallowEqual);

  const hasElectricityPrepayMeter = fuelType.electric && isElecPrepayMeter;
  const hasGasPrepayMeter = fuelType.gas && isGasPrepayMeter;

  const hasAnyPrepayMeter = hasElectricityPrepayMeter || hasGasPrepayMeter;

  const hasElectricityMeter = !!electricityMeter;
  const electricityErrors = useSelector(selectElectricityErrors, shallowEqual);
  const isUnsupportedElectricityMeter = includesErrorCodes(
    electricityErrors,
    // this error is supported. keep in config to trigger the select options page
    unsupportedMeterErrors.filter(
      (error) => error !== METER_TYPE_ERROR_CODES.MeterNonDomesticClassException
    )
  );
  const isUneligibleElectricityMeter = includesErrorCodes(
    electricityErrors,
    UNELIGIBLE_METER_ERRORS
  );
  const isEvException = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterEVException
  ]);

  const hasElecE7Meter = useSelector(selectHasE7Meter, shallowEqual);
  const hasE7Meter = !!(fuelType.electric && hasElecE7Meter); //For gas fuel cannot have two rate meter
  const isElecTOUMeter = useSelector(
    selectHasElectricityTOUMeter,
    shallowEqual
  );
  const isGasTOUMeter = useSelector(selectHasGasTOUMeter, shallowEqual);
  const hasElectricityTOUMeter = fuelType.electric && isElecTOUMeter;
  const hasGasTOUMeter = fuelType.gas && isGasTOUMeter;
  const hasAnyTOUMeter = hasElectricityTOUMeter || hasGasTOUMeter;

  const hasGasMeter = !!gasMeter;
  const gasErrors = useSelector(selectGasErrors, shallowEqual);
  const isUnsupportedGasMeter = includesErrorCodes(
    gasErrors,
    unsupportedMeterErrors.filter(
      (error) => error !== METER_TYPE_ERROR_CODES.MeterNonDomesticClassException
    )
  );
  const isUneligibleGasMeter = includesErrorCodes(
    gasErrors,
    UNELIGIBLE_METER_ERRORS
  );

  const isBGECustomer = useSelector(selectIsBGECustomer, shallowEqual);
  const isBGCustomer = useSelector(selectIsBGCustomer, shallowEqual);
  const isMeterNonBGExportException = useSelector(
    selectMeterNonBGExportException,
    shallowEqual
  );
  const isMeterpointBGExportException = useSelector(
    selectMeterBGExportException,
    shallowEqual
  );
  const isBGCustomerForGas = includesErrorCodes(gasErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException
  ]);
  const isBGCustomerForElec = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierBGREException
  ]);
  const isBGECustomerForGas = includesErrorCodes(gasErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException
  ]);
  const isBGECustomerForElec = includesErrorCodes(electricityErrors, [
    METER_TYPE_ERROR_CODES.MeterCurrentSupplierNEPException
  ]);

  const allMeterErrors = [...electricityErrors, ...gasErrors];
  const meterTypeErrors = allMeterErrors.filter(({ code }) =>
    unsupportedMeterErrors.includes(code)
  );

  const selectedFuelErrors = [
    ...(fuelType.gas ? gasErrors : []),
    ...(fuelType.electric ? electricityErrors : [])
  ].filter(({ code }) => unsupportedMeterErrors.includes(code));

  const regionCode = electricityMeter?.regionCode;

  return {
    electricityMeter,
    electricityMeters,
    gasMeter,
    hasElectricityMeter,
    hasGasMeter,
    hasE7Meter,
    hasElectricityTOUMeter,
    hasAnyPrepayMeter,
    hasAnyTOUMeter,
    hasElectricityPrepayMeter,
    hasRelatedElectricityMeters,
    hasGasPrepayMeter,
    isGasPrepayMeter,
    isElecPrepayMeter,
    hasGasTOUMeter,
    isUnsupportedElectricityMeter,
    isEvException,
    isUneligibleElectricityMeter,
    isUnsupportedGasMeter,
    isUneligibleGasMeter,
    meterTypeErrors,
    allMeterErrors,
    electricityErrors,
    gasErrors,
    isBGECustomer,
    isMeterpointValidationRequired,
    isBGCustomer,
    isMeterNonBGExportException,
    isMeterpointBGExportException,
    selectedFuelErrors,
    isBGCustomerForElec,
    isBGECustomerForElec,
    isBGECustomerForGas,
    isBGCustomerForGas,
    isRelatedElectricityMeter,
    regionCode
  };
};
